import { useNavigate, useParams } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Save, Send, Bold, Italic, Heading, List, Quote, Image, MapPin, X, Plus } from 'lucide-react';
import { useState } from 'react';

const TravelogueEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = id && id !== 'new';
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [destination, setDestination] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [checkpoints, setCheckpoints] = useState([]);
  const [showQuitDialog, setShowQuitDialog] = useState(false);

  const addTag = () => { if (tagInput.trim() && !tags.includes(tagInput.trim())) { setTags([...tags, tagInput.trim()]); setTagInput(''); } };
  const removeTag = (t) => setTags(tags.filter((x) => x !== t));
  const addCheckpoint = () => setCheckpoints([...checkpoints, { name: '', time: '', note: '' }]);
  const updateCheckpoint = (i, field, value) => { const cp = [...checkpoints]; cp[i][field] = value; setCheckpoints(cp); };
  const removeCheckpoint = (i) => setCheckpoints(checkpoints.filter((_, idx) => idx !== i));

  return (
    <div className="min-h-screen bg-bg-primary">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 md:px-6 pt-24 pb-20">
        <div className="flex items-center justify-between mb-6 sticky top-20 py-2 z-10" style={{ background: 'rgba(6, 16, 32, 0.92)', backdropFilter: 'blur(16px)' }}>
          <Button variant="ghost" onClick={() => setShowQuitDialog(true)} className="text-text-secondary hover:text-primary"><ArrowLeft className="w-4 h-4 mr-1" /> 返回</Button>
          <h1 className="text-lg font-bold text-text-primary">{isEdit ? '编辑游记' : '新建游记'}</h1>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-btn border-[rgba(143,169,192,0.2)] text-text-secondary"><Save className="w-4 h-4 mr-1" /> 保存草稿</Button>
            <Button className="bg-primary hover:bg-primary-hover text-white rounded-btn shadow-btn"><Send className="w-4 h-4 mr-1" /> 发布</Button>
          </div>
        </div>

        <div className="space-y-4">
          <Card className="border-0 shadow-card rounded-card" style={{ background: 'rgba(8, 21, 38, 0.55)', backdropFilter: 'blur(12px)', border: '1px solid rgba(143,169,192,0.12)' }}>
            <CardContent className="p-5 space-y-4">
              <div className="border-2 border-dashed border-[rgba(143,169,192,0.2)] rounded-card h-40 flex flex-col items-center justify-center text-text-tertiary hover:border-primary hover:text-primary transition-colors cursor-pointer">
                <Image className="w-8 h-8 mb-2" />
                <p className="text-sm">点击或拖拽上传封面图</p>
              </div>
              <Input placeholder="给你的游记起个标题" value={title} onChange={(e) => setTitle(e.target.value)} className="text-xl font-bold border-0 px-0 focus-visible:ring-0 placeholder:text-text-tertiary" />
              <Input placeholder="目的地（如：大理）" value={destination} onChange={(e) => setDestination(e.target.value)} className="rounded-smwidget border-[rgba(143,169,192,0.15)]" />
              <div className="flex items-center gap-2">
                <Input placeholder="添加标签，回车确认" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addTag()} className="rounded-smwidget border-[rgba(143,169,192,0.15)]" />
                <Button variant="outline" size="sm" onClick={addTag} className="rounded-smwidget border-[rgba(143,169,192,0.2)]"><Plus className="w-4 h-4" /></Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {tags.map((t) => <span key={t} className="text-xs bg-primary-light text-primary px-2 py-1 rounded-full flex items-center gap-1">{t} <button onClick={() => removeTag(t)}><X className="w-3 h-3" /></button></span>)}
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-card rounded-card" style={{ background: 'rgba(8, 21, 38, 0.55)', backdropFilter: 'blur(12px)', border: '1px solid rgba(143,169,192,0.12)' }}>
            <CardContent className="p-5">
              <div className="flex gap-2 mb-3 pb-3 border-b border-[rgba(143,169,192,0.12)]">
                <button className="p-1.5 rounded hover:bg-[rgba(143,169,192,0.1)] text-text-secondary"><Bold className="w-4 h-4" /></button>
                <button className="p-1.5 rounded hover:bg-[rgba(143,169,192,0.1)] text-text-secondary"><Italic className="w-4 h-4" /></button>
                <button className="p-1.5 rounded hover:bg-[rgba(143,169,192,0.1)] text-text-secondary"><Heading className="w-4 h-4" /></button>
                <button className="p-1.5 rounded hover:bg-[rgba(143,169,192,0.1)] text-text-secondary"><List className="w-4 h-4" /></button>
                <button className="p-1.5 rounded hover:bg-[rgba(143,169,192,0.1)] text-text-secondary"><Quote className="w-4 h-4" /></button>
                <button className="p-1.5 rounded hover:bg-[rgba(143,169,192,0.1)] text-text-secondary"><Image className="w-4 h-4" /></button>
              </div>
              <textarea placeholder="开始书写你的旅行故事...（支持Markdown）" value={content} onChange={(e) => setContent(e.target.value)} className="w-full min-h-[500px] resize-none focus:outline-none text-text-primary leading-relaxed placeholder:text-text-tertiary" />
            </CardContent>
          </Card>

          <Card className="border-0 shadow-card rounded-card" style={{ background: 'rgba(8, 21, 38, 0.55)', backdropFilter: 'blur(12px)', border: '1px solid rgba(143,169,192,0.12)' }}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-text-primary flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" /> 打卡点管理</h3>
                <Button variant="outline" size="sm" onClick={addCheckpoint} className="rounded-smwidget border-[rgba(143,169,192,0.2)]"><Plus className="w-4 h-4 mr-1" /> 添加</Button>
              </div>
              <div className="space-y-3">
                {checkpoints.map((cp, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input placeholder="地点名" value={cp.name} onChange={(e) => updateCheckpoint(i, 'name', e.target.value)} className="rounded-smwidget border-[rgba(143,169,192,0.15)] flex-1" />
                    <Input placeholder="时间" value={cp.time} onChange={(e) => updateCheckpoint(i, 'time', e.target.value)} className="rounded-smwidget border-[rgba(143,169,192,0.15)] w-32" />
                    <Button variant="ghost" size="sm" onClick={() => removeCheckpoint(i)} className="text-danger"><X className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-card rounded-card" style={{ background: 'rgba(8, 21, 38, 0.55)', backdropFilter: 'blur(12px)', border: '1px solid rgba(143,169,192,0.12)' }}>
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <Label className="text-text-primary font-medium">公开可见</Label>
                <p className="text-xs text-text-tertiary">关闭后仅自己可见</p>
              </div>
              <Switch checked={isPublic} onCheckedChange={setIsPublic} />
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={showQuitDialog} onOpenChange={setShowQuitDialog}>
        <DialogContent className="rounded-card">
          <DialogHeader><DialogTitle className="text-text-primary">确认放弃编辑？</DialogTitle></DialogHeader>
          <p className="text-sm text-text-secondary mb-4">未保存的内容将会丢失。</p>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setShowQuitDialog(false)} className="rounded-btn">继续编辑</Button>
            <Button onClick={() => navigate('/travelogues')} className="bg-danger hover:bg-danger/90 text-white rounded-btn">放弃</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TravelogueEditor;
